import type { PostHog } from 'posthog-js'
import env from '@/infra/env'
import { Id } from '@/infra/id'
import type { WorkspaceMember } from '@/features/iam/members/WorkspaceMember'
import type { TrackingService } from '@/features/tracking/TrackingService'
import { concatMap, from, of, Subject } from 'rxjs'
import { tap } from 'rxjs/operators'

export class PostHogTrackingService implements TrackingService {
  /**
   * Functions to call with the PostHog instance once initialized.
   *
   * @private
   */
  private readonly _postHogCalls$ = new Subject<(postHog: PostHog) => unknown>()

  /**
   * PostHog instance.
   * @private
   */
  private _postHog: PostHog | null = null

  constructor() {
    // Loads PostHog when a method is to be called on it.
    this._postHogCalls$
      .pipe(concatMap((call) => this._acquirePostHog().pipe(tap(call))))
      .subscribe()

    // Immediately initialize PostHog but in the background.
    this._call(() => void 0)
  }

  /** @inheritDoc */
  identify(member: WorkspaceMember) {
    this._call((postHog) => {
      postHog.identify(Id.assert(member.user.id), {
        first_name: member.user.givenName,
        last_name: member.user.familyName,
        name: member.user.displayName,
        email: member.user.email,
        workspace_id: Id.assert(member.workspace.id),
        id: Id.assert(member.user.id),
      })
    })
  }

  /** @inheritDoc */
  reset() {
    if (this._postHog) {
      // No point in resetting if we never initialized it.
      this._postHog.reset()
    }
  }

  /**
   * Calls the specified function on the PostHog SDK.
   * If the SDK has not loaded yet, it will be loaded first, then the function
   * will be called afterward.
   *
   * If multiple calls are queued, they will be executed in order.
   *
   * @param fn
   * @private
   */
  private _call(fn: (instance: PostHog) => unknown) {
    this._postHogCalls$.next(fn)
  }

  /**
   * Acquires the PostHog SDK by initializing it once and then caching it.
   *
   * @private
   */
  private _acquirePostHog() {
    if (this._postHog) {
      return of(this._postHog)
    }

    return from(this._init())
  }

  /**
   * Initializes the PostHog SDK.
   * @private
   */
  private async _init() {
    const { posthog } = await import('posthog-js')
    const instance = posthog.init(env.POSTHOG_KEY, {
      api_host: 'https://app.posthog.com',
    })
    this._postHog = instance
    return instance
  }

  /**
   * Creates the PostHog tracking service.
   */
  static create() {
    return new PostHogTrackingService()
  }
}
